import React, { useState } from "react";

import { Button } from "../../../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Input } from "../../../components/ui/input";

interface FontSelectorProps {
  selectedTitleFont?: string;
  selectedBodyFont?: string;
  onTitleFontChange?: (font: string) => void;
  onBodyFontChange?: (font: string) => void;
}

// Fontes organizadas por categoria e uso
const FONT_CATEGORIES = {
  "Títulos (Display)": [
    { name: "Oswald", value: "Oswald, sans-serif", description: "Condensada e impactante" },
    { name: "Bebas Neue", value: "Bebas Neue, sans-serif", description: "Bold e moderna" },
    { name: "Anton", value: "Anton, sans-serif", description: "Condensada e forte" },
    { name: "Righteous", value: "Righteous, cursive", description: "Arredondada e divertida" },
    { name: "Fredoka One", value: "Fredoka One, cursive", description: "Arredondada e amigável" },
    { name: "Lobster", value: "Lobster, cursive", description: "Script elegante" },
    { name: "Playfair Display", value: "Playfair Display, serif", description: "Elegante e decorativa" },
    { name: "Merriweather", value: "Merriweather, serif", description: "Otimizada para leitura" },
  ],
  "Corpo do Texto (Sans-serif)": [
    { name: "Inter", value: "Inter, sans-serif", description: "Moderno e limpo" },
    { name: "Roboto", value: "Roboto, sans-serif", description: "Google Fonts" },
    { name: "Open Sans", value: "Open Sans, sans-serif", description: "Legível e versátil" },
    { name: "Lato", value: "Lato, sans-serif", description: "Elegante e profissional" },
    { name: "Montserrat", value: "Montserrat, sans-serif", description: "Geométrico e moderno" },
    { name: "Poppins", value: "Poppins, sans-serif", description: "Arredondado e amigável" },
    { name: "Source Sans Pro", value: "Source Sans Pro, sans-serif", description: "Adobe, profissional" },
    { name: "Nunito", value: "Nunito, sans-serif", description: "Suave e arredondado" },
    { name: "Raleway", value: "Raleway, sans-serif", description: "Elegante e sofisticado" },
    { name: "Ubuntu", value: "Ubuntu, sans-serif", description: "Humanista e amigável" },
    { name: "Work Sans", value: "Work Sans, sans-serif", description: "Otimizada para interfaces" },
    { name: "DM Sans", value: "DM Sans, sans-serif", description: "Minimalista e clara" },
  ],
  "Corpo do Texto (Serif)": [
    { name: "Lora", value: "Lora, serif", description: "Equilibrada e legível" },
    { name: "PT Serif", value: "PT Serif, serif", description: "Russa, clássica" },
    { name: "Crimson Text", value: "Crimson Text, serif", description: "Tradicional e elegante" },
    { name: "Libre Baskerville", value: "Libre Baskerville, serif", description: "Inspirada na Baskerville" },
    { name: "Source Serif Pro", value: "Source Serif Pro, serif", description: "Adobe, profissional" },
    { name: "Cormorant Garamond", value: "Cormorant Garamond, serif", description: "Clássica e refinada" },
  ],
};

function FontPicker({
  label,
  description,
  selectedFont,
  onFontChange,
  fontCategory,
}: {
  label: string;
  description: string;
  selectedFont?: string;
  onFontChange?: (font: string) => void;
  fontCategory: keyof typeof FONT_CATEGORIES;
}) {
  const [showSelector, setShowSelector] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const selectedFontOption = FONT_CATEGORIES[fontCategory]?.find(font => font.value === selectedFont);

  const filteredFonts = FONT_CATEGORIES[fontCategory]?.filter(font =>
    font.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    font.description.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  return (
    <div className="space-y-4">
      <div>
        <h4 className="text-md font-semibold mb-1 text-gray-900 dark:text-gray-100">{label}</h4>
        <p className="text-sm text-gray-600 dark:text-gray-300">{description}</p>
      </div>

      {/* Preview da fonte atual */}
      <div className="p-4 border rounded-lg bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div
              className="text-xl font-bold text-gray-900 dark:text-gray-100"
              style={{ fontFamily: selectedFont || "Inter, sans-serif" }}
            >
              {selectedFontOption?.name || "Inter"}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {selectedFontOption?.description || "Moderno e limpo"}
            </div>
          </div>
          <Button
            type="button"
            onClick={() => setShowSelector(!showSelector)}
            variant={showSelector ? "outline" : "default"}
            size="sm"
            className="px-4"
          >
            {showSelector ? "Fechar" : "Escolher"}
          </Button>
        </div>
        
        <div className="space-y-2">
          <div
            className="text-lg font-bold text-gray-900 dark:text-gray-100"
            style={{ fontFamily: selectedFont || "Inter, sans-serif" }}
          >
            {label === "Fonte dos Títulos" ? "Título Principal" : "Texto do Corpo"}
          </div>
          <div
            className="text-sm text-gray-600 dark:text-gray-300"
            style={{ fontFamily: selectedFont || "Inter, sans-serif" }}
          >
            {label === "Fonte dos Títulos" 
              ? "Subtítulo da Seção" 
              : "Este é um exemplo de texto corrido para mostrar como a fonte ficará na landing page."
            }
          </div>
        </div>
      </div>

      {/* Seletor de fontes */}
      {showSelector && (
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle className="text-gray-900 dark:text-gray-100">
              Escolher {label}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Campo de busca */}
            <div className="relative">
              <Input
                type="text"
                placeholder="Buscar fonte..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                🔍
              </div>
            </div>

            {/* Lista de fontes */}
            <div className="max-h-64 overflow-y-auto space-y-2">
              {filteredFonts.length === 0 ? (
                <div className="text-center py-6 text-gray-500 dark:text-gray-400">
                  Nenhuma fonte encontrada para "{searchTerm}"
                </div>
              ) : (
                filteredFonts.map((font) => (
                  <button
                    key={font.value}
                    type="button"
                    onClick={() => {
                      onFontChange?.(font.value);
                      setShowSelector(false);
                    }}
                    className={`w-full p-3 rounded-lg border text-left transition-all duration-200 hover:shadow-md ${
                      selectedFont === font.value
                        ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-400 ring-2 ring-blue-200 dark:ring-blue-800"
                        : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800"
                    }`}
                    style={{ fontFamily: font.value }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="font-bold text-base mb-1 text-gray-900 dark:text-gray-100">{font.name}</div>
                        <div className="text-sm text-gray-600 dark:text-gray-300">{font.description}</div>
                      </div>
                      {selectedFont === font.value && (
                        <div className="ml-4 text-blue-600 dark:text-blue-400 text-lg">✓</div>
                      )}
                    </div>
                  </button>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export function FontSelector({ 
  selectedTitleFont, 
  selectedBodyFont, 
  onTitleFontChange, 
  onBodyFontChange 
}: FontSelectorProps) {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-gray-100">
          Fontes da Landing Page
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
          Configure as fontes separadamente para títulos e corpo do texto. Isso permite maior controle sobre a hierarquia visual.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Fonte dos Títulos */}
        <FontPicker
          label="Fonte dos Títulos"
          description="Usada em títulos principais, subtítulos e elementos de destaque"
          selectedFont={selectedTitleFont}
          onFontChange={onTitleFontChange}
          fontCategory="Títulos (Display)"
        />

        {/* Fonte do Corpo */}
        <FontPicker
          label="Fonte do Corpo"
          description="Usada em parágrafos, descrições e textos corridos"
          selectedFont={selectedBodyFont}
          onFontChange={onBodyFontChange}
          fontCategory="Corpo do Texto (Sans-serif)"
        />
      </div>

      {/* Preview Combinado */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="text-gray-900 dark:text-gray-100">Preview Combinado</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4 p-4 border rounded-lg bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900">
            <div
              className="text-3xl font-bold text-gray-900 dark:text-gray-100"
              style={{ fontFamily: selectedTitleFont || "Inter, sans-serif" }}
            >
              Título Principal da Landing Page
            </div>
            <div
              className="text-xl font-semibold text-gray-800 dark:text-gray-200"
              style={{ fontFamily: selectedTitleFont || "Inter, sans-serif" }}
            >
              Subtítulo da Seção
            </div>
            <div
              className="text-base text-gray-700 dark:text-gray-300"
              style={{ fontFamily: selectedBodyFont || "Inter, sans-serif" }}
            >
              Este é um exemplo de texto corrido para mostrar como a combinação de fontes ficará na landing page. 
              A hierarquia tipográfica é fundamental para uma boa experiência do usuário e comunicação eficaz.
            </div>
            <div
              className="text-sm text-gray-600 dark:text-gray-400"
              style={{ fontFamily: selectedBodyFont || "Inter, sans-serif" }}
            >
              Texto secundário e informações adicionais também seguem a fonte do corpo para manter consistência.
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}